// src/pages/authn/SessionsPage.tsx
// Active sessions + login history.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useAuthn, useAuthnAuth, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, CardFooter, Input, Alert } from '@react/components';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { ErrorAlert } from '@react/components/ErrorAlert';
import { SessionCard } from '../../components/SessionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { formatSessionInfo, formatLoginResult } from '../../features/authn/logic';
import type { SSOSession, LoginRecord } from '@core/types/authn';
import type { PaginatedRequest } from '@core/types/pagination';

function formatTimestamp(ts: string | undefined): string {
  if (!ts) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(ts));
  } catch {
    return ts;
  }
}

export const SessionsPage = observer(() => {
  const authnStore = useAuthn();
  const authnAuth = useAuthnAuth();
  const { t } = useI18n();

  const [sessions, setSessions] = useState<SSOSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Logout-everywhere is destructive and requires re-entering the password
  // (VerifyPassword) plus a typed confirmation before InvalidateAllTokens fires.
  const LOGOUT_KEYWORD = 'LOGOUT';
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [logoutPassword, setLogoutPassword] = useState('');
  const [logoutConfirmText, setLogoutConfirmText] = useState('');
  const [logoutError, setLogoutError] = useState<unknown>(null);
  const [logoutStep, setLogoutStep] = useState<'verify' | 'confirm'>('verify');
  const [logoutBusy, setLogoutBusy] = useState(false);

  useEffect(() => {
    loadSessions();
    authnAuth.ListLoginRecords({ pagination: { limit: 20, cursor: '', direction: '' } } as PaginatedRequest).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      await authnStore.ListSSOSessions();
      setSessions(authnStore.items as SSOSession[]);
    } catch (err) {
      setError(err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleRevoke = async (sessionId: string) => {
    try {
      await authnStore.TerminateSSOSession(sessionId, { entity: {} as never });
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
    } catch (err) {
      setError(err);
    }
  };

  const openLogoutAll = () => {
    setLogoutStep('verify');
    setLogoutPassword('');
    setLogoutConfirmText('');
    setLogoutError(null);
    setLogoutAllOpen(true);
  };

  const closeLogoutAll = () => {
    setLogoutAllOpen(false);
    setLogoutPassword('');
    setLogoutConfirmText('');
    setLogoutError(null);
  };

  const handleVerifyThenConfirm = async () => {
    if (!logoutPassword) return;
    setLogoutBusy(true);
    setLogoutError(null);
    try {
      await authnAuth.VerifyPassword({ password: logoutPassword, action: 'invalidate_all_tokens' });
      setLogoutStep('confirm');
    } catch (err) {
      setLogoutError(err);
    } finally {
      setLogoutBusy(false);
    }
  };

  const handleInvalidateAll = async () => {
    setLogoutBusy(true);
    setLogoutError(null);
    try {
      await authnAuth.InvalidateAllTokens({});
      setSessions([]);
      closeLogoutAll();
    } catch (err) {
      setLogoutError(err);
    } finally {
      setLogoutBusy(false);
    }
  };

  const loginRecords = authnAuth.items as LoginRecord[];
  const loginLoading = authnAuth.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('security.sessionsTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('security.sessionsSubtitle')}</p>
        </div>
        {sessions.length > 0 && (
          <Button variant="danger" size="sm" onClick={openLogoutAll}>
            {t('security.logoutAllDevices')}
          </Button>
        )}
      </div>

      {logoutAllOpen && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive">{t('security.logoutAllDevices')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!!logoutError && <ErrorAlert error={logoutError} dismissible onDismiss={() => setLogoutError(null)} />}
            {logoutStep === 'verify' ? (
              <>
                <Alert variant="warning">{t('security.invalidateAllConfirm')}</Alert>
                <Input
                  label={t('security.currentPassword')}
                  type="password"
                  value={logoutPassword}
                  onChange={(e) => setLogoutPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {t('security.logoutAllTypeToConfirm', { keyword: LOGOUT_KEYWORD })}
                </p>
                <input
                  type="text"
                  value={logoutConfirmText}
                  onChange={(e) => setLogoutConfirmText(e.target.value)}
                  placeholder={LOGOUT_KEYWORD}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono"
                  autoComplete="off"
                />
              </>
            )}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={closeLogoutAll} disabled={logoutBusy}>
              {t('common.cancel')}
            </Button>
            {logoutStep === 'verify' ? (
              <Button variant="danger" size="sm" onClick={handleVerifyThenConfirm} isLoading={logoutBusy} disabled={!logoutPassword}>
                {t('common.next')}
              </Button>
            ) : (
              <Button
                variant="danger"
                size="sm"
                onClick={handleInvalidateAll}
                isLoading={logoutBusy}
                disabled={logoutConfirmText !== LOGOUT_KEYWORD}
              >
                {t('security.logoutAllDevices')}
              </Button>
            )}
          </CardFooter>
        </Card>
      )}

      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('security.activeSessions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {sessionsLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth={2} />
                  <line x1="8" y1="21" x2="16" y2="21" strokeWidth={2} />
                  <line x1="12" y1="17" x2="12" y2="21" strokeWidth={2} />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">{t('security.noActiveSessions')}</p>
            </div>
          ) : (
            sessions.map((session, i) => {
              const info = formatSessionInfo(session.user_agent ?? '');
              return (
                <SessionCard
                  key={session.session_id}
                  device={info.device}
                  browser={info.browser}
                  os={info.os}
                  ip={session.ip_address ?? ''}
                  lastActive={formatTimestamp(session.last_accessed_at ?? session.created_at)}
                  isCurrent={i === 0}
                  onRevoke={i > 0 ? () => handleRevoke(session.session_id) : undefined}
                  revokeLabel={t('security.revokeSession')}
                  currentLabel={t('security.currentSession')}
                />
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Login History */}
      <Card>
        <CardHeader>
          <CardTitle>{t('security.loginHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loginLoading && loginRecords.length === 0 ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 rounded bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <Table hoverable>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('security.dateTime')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead>{t('security.ipAddress')}</TableHead>
                    <TableHead>{t('security.device')}</TableHead>
                    <TableHead>{t('security.details')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginRecords.length === 0 && (
                    <TableEmpty colSpan={5} message={t('security.noLoginHistory')} />
                  )}
                  {loginRecords.map((record) => {
                    const result = formatLoginResult(record.result ?? '');
                    const info = formatSessionInfo(record.user_agent ?? '');
                    return (
                      <TableRow key={record.entity?.id}>
                        <TableCell className="text-sm">{formatTimestamp(record.timestamp)}</TableCell>
                        <TableCell>
                          <StatusBadge variant={result.variant} label={t(result.labelKey)} size="sm" />
                        </TableCell>
                        <TableCell className="text-sm font-mono">{record.ip_address || '-'}</TableCell>
                        <TableCell className="text-sm">{info.browser} / {info.os}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{record.failure_reason || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {authnAuth.hasMore && (
                <div className="mt-4 text-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => authnAuth.ListLoginRecords({ pagination: { limit: 20, cursor: authnAuth.pageInfo?.next_cursor ?? '', direction: '' } } as PaginatedRequest)}
                    isLoading={authnAuth.isLoading}
                  >
                    {t('common.loadMore')}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default SessionsPage;
